import { useEffect, useRef, useState } from "react";
import { AskAiIcon } from "./AskAiIcon";
import { QrBadgeIcon } from "./QrBadgeIcon";
import { UserMenu } from "./UserMenu";

export type WorkspaceTab = "account" | "contacts" | "campaigns" | "floor" | "profile";

export function WorkspaceTabs({
  mainTab,
  onSelectTab,
}: {
  mainTab: WorkspaceTab;
  onSelectTab: (tab: WorkspaceTab) => void;
}) {
  return (
    <div className="main-tabs" role="tablist" aria-label="Workspace">
      <button
        type="button"
        role="tab"
        className={mainTab === "account" ? "tab on" : "tab"}
        aria-selected={mainTab === "account"}
        onClick={() => onSelectTab("account")}
      >
        Account
      </button>
      <button
        type="button"
        role="tab"
        className={mainTab === "contacts" ? "tab on" : "tab"}
        aria-selected={mainTab === "contacts"}
        onClick={() => onSelectTab("contacts")}
      >
        Contacts
      </button>
      <button
        type="button"
        role="tab"
        className={mainTab === "campaigns" ? "tab on" : "tab"}
        aria-selected={mainTab === "campaigns"}
        onClick={() => onSelectTab("campaigns")}
      >
        Campaigns
      </button>
      <button
        type="button"
        role="tab"
        className={mainTab === "floor" ? "tab on" : "tab"}
        aria-selected={mainTab === "floor"}
        onClick={() => onSelectTab("floor")}
      >
        Floor
      </button>
    </div>
  );
}

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      {open ? (
        <path
          fill="currentColor"
          d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3 10.6 10.6 16.9 4.3z"
        />
      ) : (
        <path fill="currentColor" d="M3 6h18v2H3zm0 5h18v2H3zm0 5h18v2H3z" />
      )}
    </svg>
  );
}

export function MainNav({
  mainTab,
  onSelectTab,
  photoUrl,
  displayName,
  onAppDownload,
  onSignOut,
  healthTone,
  healthLabel,
  healthTitle,
  onRefreshHealth,
  scannerOpen,
  askAiOpen,
  onScanBadge,
  onAskAi,
}: {
  mainTab: WorkspaceTab;
  onSelectTab: (tab: WorkspaceTab) => void;
  photoUrl: string | null;
  displayName: string;
  onAppDownload?: () => void;
  onSignOut: () => void;
  healthTone: string;
  healthLabel: string;
  healthTitle: string;
  onRefreshHealth: () => void;
  scannerOpen: boolean;
  askAiOpen: boolean;
  onScanBadge: () => void;
  onAskAi: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const select = (tab: WorkspaceTab) => {
    setOpen(false);
    onSelectTab(tab);
  };

  return (
    <div className="topbar-actions">
      <button
        type="button"
        className={`health-dot ${healthTone}`}
        onClick={onRefreshHealth}
        aria-label={healthLabel}
        title={healthTitle}
      />
      <button
        type="button"
        className={`map-control-btn topbar-tool-btn${scannerOpen ? " on" : ""}`}
        aria-label="Scan badge"
        title="Scan badge"
        onClick={onScanBadge}
      >
        <QrBadgeIcon size={18} />
      </button>
      <button
        type="button"
        className={`map-control-btn topbar-tool-btn${askAiOpen ? " on" : ""}`}
        aria-label="Ask AI"
        title="Ask AI"
        onClick={onAskAi}
      >
        <AskAiIcon size={18} />
      </button>
      <UserMenu
        photoUrl={photoUrl}
        displayName={displayName}
        onProfile={() => onSelectTab("profile")}
        onAppDownload={onAppDownload}
        onSignOut={onSignOut}
      />
      <div className="hamburger-nav" ref={rootRef}>
        <button
          type="button"
          className={`hamburger-btn${open ? " on" : ""}`}
          aria-label="Workspace menu"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls="workspace-hamburger-menu"
          onClick={() => setOpen((value) => !value)}
        >
          <HamburgerIcon open={open} />
        </button>
        {open ? (
          <div id="workspace-hamburger-menu" className="hamburger-dropdown" role="menu">
            <button
              type="button"
              role="menuitem"
              className={mainTab === "account" ? "on" : ""}
              aria-current={mainTab === "account" ? "page" : undefined}
              onClick={() => select("account")}
            >
              Account
            </button>
            <button
              type="button"
              role="menuitem"
              className={mainTab === "contacts" ? "on" : ""}
              aria-current={mainTab === "contacts" ? "page" : undefined}
              onClick={() => select("contacts")}
            >
              Contacts
            </button>
            <button
              type="button"
              role="menuitem"
              className={mainTab === "campaigns" ? "on" : ""}
              aria-current={mainTab === "campaigns" ? "page" : undefined}
              onClick={() => select("campaigns")}
            >
              Campaigns
            </button>
            <button
              type="button"
              role="menuitem"
              className={mainTab === "floor" ? "on" : ""}
              aria-current={mainTab === "floor" ? "page" : undefined}
              onClick={() => select("floor")}
            >
              Floor
            </button>
            <div className="hamburger-sep" role="separator" />
            <button
              type="button"
              role="menuitem"
              className={mainTab === "profile" ? "on" : ""}
              aria-current={mainTab === "profile" ? "page" : undefined}
              onClick={() => select("profile")}
            >
              Profile
            </button>
            {onAppDownload ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onAppDownload();
                }}
              >
                App Download
              </button>
            ) : null}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
            >
              Sign out
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
